import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { useGetSchemaDatasetByAnalysis } from "@/api/analysis/useAnalysisAPI";
import { useGetDatasetMetadataQuery } from "@/api/dataset/useDatasetAPI";
import { SpinnerIcon } from "@/assets/icons";
import { mapAnalysisSchemaToDetailSchema } from "@/helpers/datasetAttributeHelper";
import useOnClickOutside from "@/hooks/ui/useClickOutside";
import { RootState } from "@/store";
import { Dataset, DatasetDetailResponse } from "@/types/dataset";

import { twMerge } from "tailwind-merge";

import EditPopupAttribute from "./EditPopupAttribute";

// Fixed left (px); top always 30px. `catchment` avoids overlapping its panel.
export const OFFSET_MAPPING = {
  default: { left: 430 },
  catchment: { left: 784 }
};

interface Props {
  dataset: Dataset;
  offsetId?: keyof typeof OFFSET_MAPPING;
  onClose: () => void;
}

export const EditPopup = ({
  dataset,
  offsetId = "default",
  onClose
}: Props) => {
  const { showAnalysisResultPopup } = useSelector(
    (state: RootState) => state.ui
  );
  const { rawDatasets } = useSelector((state: RootState) => state.dataset);
  const { latestAnalysisId } = useSelector((state: RootState) => state.spatial);

  const { data: datasetSchema } = useGetSchemaDatasetByAnalysis(
    latestAnalysisId,
    dataset.data.id,
    {
      enabled: showAnalysisResultPopup && !!latestAnalysisId
    }
  );

  const mappedDatasetMetaSchema: Dataset = {
    ...dataset,
    data: {
      ...dataset.data,
      schema: mapAnalysisSchemaToDetailSchema(
        datasetSchema?.schema,
        rawDatasets,
        dataset.data.id
      )
    }
  };

  const dialogRef = useRef<HTMLDialogElement>(null);

  const [overlayElement, setOverlayElement] = useState<HTMLElement | null>(
    null
  );

  const { schema: _schema, ...datasetWithoutSchema } = dataset.data;
  const { data: selectedDatasetMetadata } = useGetDatasetMetadataQuery(
    datasetWithoutSchema,
    {
      enabled: !latestAnalysisId && !showAnalysisResultPopup // only fetch when activeDatasetId exists and not showing analysis result popup
    }
  );

  useEffect(() => {
    // Safely access DOM element after component mounts
    setOverlayElement(document.getElementById("overlays"));
  }, []);

  useOnClickOutside(dialogRef, onClose, [overlayElement]);

  const datasetOriginal: Dataset = showAnalysisResultPopup
    ? mappedDatasetMetaSchema
    : dataset;
  const selectedDataset: DatasetDetailResponse | null = showAnalysisResultPopup
    ? (mappedDatasetMetaSchema.data ?? null)
    : (selectedDatasetMetadata ?? null);

  return (
    <dialog
      ref={dialogRef}
      open
      style={{ left: OFFSET_MAPPING[offsetId].left }}
      className={twMerge(
        "z-[99] font-sans border-gainsboro bg-white rounded-lg w-[338px]",
        "flex flex-col h-auto max-h-[calc(100vh-54px)] fixed top-[30px]"
      )}
    >
      {selectedDataset ? (
        <EditPopupAttribute
          dataset={datasetOriginal}
          selectedDataset={selectedDataset}
          onClose={onClose}
        />
      ) : (
        <SpinnerIcon
          className="my-4 justify-self-center spin-animation"
          title="edit-popup-loading"
          width={60}
          height={60}
        />
      )}
    </dialog>
  );
};

export default EditPopup;
